import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	HostBinding,
	inject,
	Input,
	OnInit,
	Output,
} from "@angular/core";
import {
	AbstractControl,
	ControlValueAccessor,
	FormGroup,
	NG_VALUE_ACCESSOR,
	UntypedFormBuilder,
} from "@angular/forms";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => TimeIntervalInputCellComponent),
	multi: true,
};

interface FormModel {
	hours: AbstractControl<number>;
	minutes: AbstractControl<number>;
}

@Component({
	selector: "ic-time-interval-input-cell",
	styleUrls: ["time-interval-input-cell.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./time-interval-input-cell.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TimeIntervalInputCellComponent implements OnInit, ControlValueAccessor {
	private cdRef = inject(ChangeDetectorRef);
	private formBuilder = inject(UntypedFormBuilder);

	@HostBinding("class.error") @Input() error = false;

	@Input() errorText = "";
	@Input() required = true;
	@Input() inputClass = "";

	@Output() blurEvent = new EventEmitter();

	disabled = false;
	form: FormGroup<FormModel>;

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	ngOnInit(): void {
		this.form = this.getForm();
	}

	private getForm(): FormGroup<FormModel> {
		return this.formBuilder.group({
			hours: [""],
			minutes: [""],
		});
	}

	writeValue(value: number) {
		if (typeof value !== "number") {
			return;
		}

		this.form.patchValue({
			hours: Math.floor(value / 60),
			minutes: value % 60,
		});

		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.form.disable();
		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	onChange() {
		if (typeof this.form.value.hours !== "number" && typeof this.form.value.minutes !== "number") {
			this.onModelChange(null);

			return;
		}

		const value = Number(this.form.value.hours) * 60 + Number(this.form.value.minutes);

		this.onModelChange(value);
	}

	onBlurred() {
		this.onTouch();
		this.blurEvent.emit();
	}
}
